import { useState, useEffect, useCallback } from 'react'
import { Avatar } from '@/components/ui/Avatar'
import { Lightbox } from '@/components/ui/Lightbox'
import { StatusDot } from '@/components/ui/StatusDot'
import { useMemberStore, selectPresence } from '@/lib/store/member-store'
import { requireClient } from '@/lib/matrix/client'
import { fetchMediaUrl } from '@/lib/utils/decrypt-attachment'
import { toast } from '@/components/ui/Toast'
import { ROLES, canKick, canBan, canSetRoles, outranks, canAssignRole } from '@/lib/matrix/membership'

interface UserProfileModalProps {
  userId: string
  roomId: string
  onClose: () => void
}

function getRoleName(powerLevel: number): string | null {
  if (powerLevel >= 100) return 'Admin'
  if (powerLevel >= 50) return 'Moderator'
  return null
}

export function UserProfileModal({ userId, roomId, onClose }: UserProfileModalProps) {
  const presence = useMemberStore(selectPresence(userId))
  const [showFullAvatar, setShowFullAvatar] = useState(false)
  const [fullAvatarUrl, setFullAvatarUrl] = useState<string | null>(null)

  const client = requireClient()
  const room = client.getRoom(roomId)
  const member = room?.getMember(userId)

  const displayName = member?.name ?? userId.replace(/^@(.+?):.*$/, '$1')
  const mxcAvatar = member?.getMxcAvatarUrl() ?? null

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (showFullAvatar) setShowFullAvatar(false)
      else onClose()
    }
  }, [onClose, showFullAvatar])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useEffect(() => {
    if (!mxcAvatar) return
    fetchMediaUrl(mxcAvatar).then(setFullAvatarUrl).catch(() => {})
  }, [mxcAvatar])
  const powerLevel = room?.currentState.getStateEvents('m.room.power_levels', '')
    ?.getContent()?.users?.[userId] ?? 0
  const role = getRoleName(powerLevel)
  const localpart = userId.replace(/^@(.+?):.*$/, '$1')
  const domain = userId.replace(/^@.+?:(.+)$/, '$1')

  const presenceStatus = presence?.presence === 'online' ? 'ok'
    : presence?.presence === 'unavailable' ? 'warn'
    : 'offline'

  const presenceLabel = presence?.presence === 'online' ? 'Online'
    : presence?.presence === 'unavailable' ? 'Away'
    : 'Offline'

  const handleDM = () => {
    onClose()
  }

  const [busy, setBusy] = useState(false)
  const me = client.getUserId()
  const isSelf = userId === me
  const membership = member?.membership ?? 'leave'

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    if (busy) return
    setBusy(true)
    try {
      await fn()
      toast(ok, 'success')
      onClose()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Action failed', 'error')
      setBusy(false)
    }
  }

  const handleKick = () => {
    if (!confirm(`Remove ${displayName} from the room?`)) return
    act(() => client.kick(roomId, userId), `Removed ${displayName}`)
  }
  const handleBan = () => {
    if (!confirm(`Ban ${displayName}? They won't be able to rejoin until unbanned.`)) return
    act(() => client.ban(roomId, userId), `Banned ${displayName}`)
  }
  const handleUnban = () => act(() => client.unban(roomId, userId), `Unbanned ${displayName}`)
  const handleRole = (level: number) =>
    act(() => client.setPowerLevel(roomId, userId, level), `Updated ${displayName}'s role`)

  const canManage = !!room && !isSelf
  const showRoles = canManage && membership === 'join' && canSetRoles(client, room!) && outranks(client, room!, userId)
  const showKick = canManage && membership === 'join' && canKick(client, room!) && outranks(client, room!, userId)
  const showBan = canManage && membership !== 'ban' && canBan(client, room!) && outranks(client, room!, userId)
  const showUnban = canManage && membership === 'ban' && canBan(client, room!)
  const currentTier = powerLevel >= 100 ? 100 : powerLevel >= 50 ? 50 : 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div
        className="w-full max-w-xs bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Banner */}
        <div className="h-20 bg-gradient-to-br from-[rgb(var(--np-accent)/0.3)] to-[rgb(var(--np-accent)/0.1)]" />

        {/* Avatar overlapping banner */}
        <div className="px-4 -mt-10">
          <div className="relative inline-block">
            <button
              onClick={() => { if (fullAvatarUrl) setShowFullAvatar(true) }}
              className={fullAvatarUrl ? 'cursor-zoom-in' : ''}
            >
              <Avatar
                mxc={mxcAvatar}
                name={displayName}
                userId={userId}
                size={80}
                className="border-4 border-[rgb(var(--np-surface))]"
              />
            </button>
            <div className="absolute bottom-1 right-1">
              <StatusDot status={presenceStatus} />
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="px-4 pt-2 pb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-[rgb(var(--np-tx-primary))]">
              {displayName}
            </h3>
            {role && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-[rgb(var(--np-accent)/0.1)] text-[rgb(var(--np-accent))]">
                {role}
              </span>
            )}
          </div>

          <p className="text-xs font-data text-[rgb(var(--np-tx-muted))] mt-0.5">
            @{localpart}
            <span className="text-[rgb(var(--np-tx-muted)/0.5)]">:{domain}</span>
          </p>

          <div className="flex items-center gap-1.5 mt-2">
            <StatusDot status={presenceStatus} />
            <span className="text-xs text-[rgb(var(--np-tx-secondary))]">{presenceLabel}</span>
            {presence?.statusMsg && (
              <span className="text-xs text-[rgb(var(--np-tx-muted))] italic truncate">
                — {presence.statusMsg}
              </span>
            )}
          </div>

          {/* Divider */}
          <div className="h-px bg-[rgb(var(--np-border))] my-3" />

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleDM}
              className="flex-1 flex items-center justify-center gap-1.5 bg-[rgb(var(--np-raised))] hover:bg-[rgb(var(--np-overlay))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2 text-xs font-medium text-[rgb(var(--np-tx-secondary))] transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Message
            </button>
            <button
              onClick={() => { navigator.clipboard.writeText(userId); onClose() }}
              className="flex items-center justify-center gap-1.5 bg-[rgb(var(--np-raised))] hover:bg-[rgb(var(--np-overlay))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2 text-xs font-medium text-[rgb(var(--np-tx-secondary))] transition-colors"
              title="Copy user ID"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
          </div>

          {/* Management — only the actions your power level permits on this member */}
          {(showRoles || showKick || showBan || showUnban) && (
            <>
              <div className="h-px bg-[rgb(var(--np-border))] my-3" />
              {showRoles && (
                <div className="mb-3">
                  <div className="label-section mb-1.5">Role</div>
                  <div className="flex gap-1.5">
                    {ROLES.map((r) => {
                      const current = currentTier === r.level
                      const allowed = current || canAssignRole(client, room!, userId, r.level)
                      return (
                        <button
                          key={r.level}
                          type="button"
                          disabled={busy || current || !allowed}
                          onClick={() => handleRole(r.level)}
                          className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            current
                              ? 'border-[rgb(var(--np-accent))] text-[rgb(var(--np-accent))] bg-[rgb(var(--np-accent)/0.1)]'
                              : 'border-[rgb(var(--np-border))] text-[rgb(var(--np-tx-secondary))] hover:bg-[rgb(var(--np-raised))]'
                          }`}
                        >
                          {r.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
              {(showKick || showBan || showUnban) && (
                <div className="flex gap-2">
                  {showKick && <DangerButton onClick={handleKick} disabled={busy}>Remove</DangerButton>}
                  {showBan && <DangerButton onClick={handleBan} disabled={busy}>Ban</DangerButton>}
                  {showUnban && <DangerButton onClick={handleUnban} disabled={busy}>Unban</DangerButton>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showFullAvatar && fullAvatarUrl && (
        <Lightbox src={fullAvatarUrl} alt={displayName} onClose={() => setShowFullAvatar(false)} />
      )}
    </div>
  )
}

function DangerButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex-1 flex items-center justify-center gap-1.5 border border-[rgb(var(--np-status-critical)/0.4)] text-[rgb(var(--np-status-critical))] hover:bg-[rgb(var(--np-status-critical)/0.1)] rounded-md px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  )
}
